import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, FloppyDisk } from "@phosphor-icons/react";
import { adminApi, getErrorMessage } from "@/lib/api";

const fmt = (iso) => new Date(iso).toLocaleString("en-AU", { timeZone: "Australia/Brisbane", dateStyle: "medium", timeStyle: "short" });
const areaCls = "w-full bg-black/40 border border-neutral-800 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#73bf44] transition-colors placeholder:text-neutral-600 resize-y";
const iconBtn = "p-1 rounded-md text-neutral-600 hover:text-white hover:bg-white/[0.06] transition-colors disabled:opacity-40";

function useNoteAction(onSaved) {
  const [busy, setBusy] = useState(false);
  const run = async (req, msg) => {
    setBusy(true);
    try { onSaved((await req()).data); toast.success(msg); return true; }
    catch (e) { toast.error(getErrorMessage(e)); return false; } finally { setBusy(false); }
  };
  return [busy, run];
}

function AddNote({ t, onSaved }) {
  const [val, setVal] = useState("");
  const [busy, run] = useNoteAction(onSaved);
  const add = async () => {
    if (val.trim() && await run(() => adminApi.post(`/admin/support-tickets/${t.id}/notes`, { text: val }), "Note added")) setVal("");
  };
  return (
    <div className="space-y-1.5">
      <textarea value={val} onChange={(e) => setVal(e.target.value)} rows={2} maxLength={5000} data-testid="ticket-note-new-input"
        onKeyDown={(e) => (e.metaKey || e.ctrlKey) && e.key === "Enter" && add()}
        placeholder="Add a note, e.g. Called customer, waiting on photos…" className={areaCls} />
      <button onClick={add} disabled={busy || !val.trim()} data-testid="ticket-note-add-btn"
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#73bf44] hover:text-white disabled:opacity-40 disabled:hover:text-[#73bf44] transition-colors">
        <Plus size={13} weight="bold" /> {busy ? "Saving…" : "Save note"}
      </button>
    </div>
  );
}

function NoteItem({ t, n, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(n.text);
  const [busy, run] = useNoteAction(onSaved);
  const url = `/admin/support-tickets/${t.id}/notes/${n.id}`;
  const save = async () => { if (await run(() => adminApi.put(url, { text: val }), "Note updated")) setEditing(false); };
  const del = () => window.confirm("Delete this note?") && run(() => adminApi.delete(url), "Note deleted");
  return (
    <li className="rounded-lg border border-neutral-800 bg-black/30 px-3 py-2" data-testid="ticket-note">
      <div className="flex items-center gap-1.5 text-[0.65rem] text-neutral-500">
        <span data-testid="ticket-note-date">{fmt(n.created_at)}</span>
        {n.updated_at && <span className="italic" data-testid="ticket-note-edited">· edited</span>}
        {!editing && (
          <div className="ml-auto flex items-center gap-0.5">
            <button onClick={() => setEditing(true)} disabled={busy} title="Edit note" data-testid="ticket-note-edit-btn" className={iconBtn}><PencilSimple size={13} /></button>
            <button onClick={del} disabled={busy} title="Delete note" data-testid="ticket-note-delete-btn" className={`${iconBtn} hover:!text-red-400`}><Trash size={13} /></button>
          </div>
        )}
      </div>
      {editing ? (
        <div className="mt-1.5 space-y-1.5">
          <textarea value={val} onChange={(e) => setVal(e.target.value)} rows={3} maxLength={5000} autoFocus data-testid="ticket-note-edit-input" className={areaCls} />
          <div className="flex items-center gap-3 text-xs font-semibold">
            <button onClick={save} disabled={busy || !val.trim() || val === n.text} data-testid="ticket-note-save-btn" className="inline-flex items-center gap-1.5 text-[#73bf44] hover:text-white disabled:opacity-40 transition-colors">
              <FloppyDisk size={13} /> {busy ? "Saving…" : "Save"}
            </button>
            <button onClick={() => { setEditing(false); setVal(n.text); }} data-testid="ticket-note-cancel-btn" className="text-neutral-500 hover:text-white transition-colors">Cancel</button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-neutral-200 whitespace-pre-wrap leading-relaxed mt-0.5" data-testid="ticket-note-text">{n.text}</p>
      )}
    </li>
  );
}

export function TicketNotes({ t, onSaved }) {
  const notes = [...(t.notes || [])].reverse();
  return (
    <div data-testid="ticket-notes">
      <div className="text-[0.62rem] uppercase tracking-[0.18em] font-bold text-neutral-500 mb-1.5" data-testid="ticket-notes-count">
        Internal notes{notes.length ? ` · ${notes.length}` : ""}
      </div>
      <AddNote t={t} onSaved={onSaved} />
      {notes.length > 0 && (
        <ul className="mt-2.5 space-y-2" data-testid="ticket-notes-list">
          {notes.map((n) => <NoteItem key={`${n.id}-${n.updated_at || ""}`} t={t} n={n} onSaved={onSaved} />)}
        </ul>
      )}
    </div>
  );
}
